import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Heart } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { bookingAPI, eventBookingAPI } from '../services/api';
import LoginSignupModal from '../components/modals/LoginSignupModal';
import { getStayInfoFromBooking } from '../utils/stayBookingDisplay';
import { isStayBookingVisibleToUser, getStayBookingBadge } from '../utils/stayBookingStatus';

const AllBookings = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [activeTab, setActiveTab] = useState('stays');
    const [stayBookings, setStayBookings] = useState([]);
    const [eventBookings, setEventBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const bookingsLoadedRef = useRef(false);

    const isEventConfirmedAndPaid = (booking) => {
        const status = String(booking.status || '').toUpperCase();
        const paymentStatus = String(booking.paymentStatus || '').toUpperCase();
        return status === 'CONFIRMED' && paymentStatus === 'SUCCEEDED';
    };

    const fetchAllBookingsOnce = useCallback(async () => {
        try {
            setLoading(true);
            const [staysResponse, eventsResponse] = await Promise.all([
                bookingAPI.getMyBookings(50, 0),
                eventBookingAPI.getMyEventBookings(50, 0)
            ]);
            setStayBookings((staysResponse.bookings || []).filter(isStayBookingVisibleToUser));
            setEventBookings((eventsResponse.bookings || []).filter(isEventConfirmedAndPaid));
        } catch (error) {
            console.error('Failed to fetch bookings:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            setShowLoginModal(true);
            return;
        }
        if (bookingsLoadedRef.current) return;
        bookingsLoadedRef.current = true;
        fetchAllBookingsOnce();
    }, [isAuthenticated, fetchAllBookingsOnce]);

    const getEventStatusBadge = (status, paymentStatus) => {
        if (status === 'CONFIRMED' && paymentStatus === 'SUCCEEDED') {
            return { text: 'Completed', bgColor: 'bg-[#10B981]', textColor: 'text-white' };
        } else if (status === 'PENDING' || paymentStatus === 'PENDING') {
            return { text: 'In Progress', bgColor: 'bg-[#8B5CF6]', textColor: 'text-white' };
        } else if (status === 'CANCELLED') {
            return { text: 'Cancelled', bgColor: 'bg-gray-400', textColor: 'text-white' };
        }
        return { text: 'In Progress', bgColor: 'bg-[#8B5CF6]', textColor: 'text-white' };
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
        });
    };

    const getCheckInTime = () => {
        return '2:00 PM';
    };

    const getCheckOutTime = () => {
        return '11:00 AM';
    };

    const calculateNights = (checkIn, checkOut) => {
        const checkInDate = new Date(checkIn);
        const checkOutDate = new Date(checkOut);
        const diffTime = Math.abs(checkOutDate - checkInDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const handleBookingClick = async (booking) => {
        if (activeTab === 'stays') {
            try {
                const bookingResponse = await bookingAPI.getBookingById(booking.bookingId);
                if (bookingResponse.booking) {
                    navigate('/booking-detail', {
                        state: {
                            bookingData: bookingResponse.booking
                        }
                    });
                }
            } catch (error) {
                console.error('Failed to fetch booking details:', error);
            }
        } else {
            // Handle event booking navigation
            try {
                const bookingResponse = await eventBookingAPI.getEventBookingById(booking.bookingId);
                if (bookingResponse.booking) {
                    navigate('/event-booking-detail', {
                        state: {
                            bookingData: bookingResponse.booking
                        }
                    });
                } else {
                    // Fallback: navigate with existing data
                    navigate('/event-booking-detail', {
                        state: {
                            bookingData: booking
                        }
                    });
                }
            } catch (error) {
                console.error('Failed to fetch event booking details:', error);
                // Fallback: navigate with existing data
                navigate('/event-booking-detail', {
                    state: {
                        bookingData: booking
                    }
                });
            }
        }
    };

    const bookings = activeTab === 'stays' ? stayBookings : eventBookings;

    if (!isAuthenticated) {
        return (
            <>
                <div className="min-h-screen bg-white">
                    <LoginSignupModal
                        isOpen={showLoginModal}
                        onClose={() => {
                            setShowLoginModal(false);
                            navigate(-1);
                        }}
                        onSuccess={() => {
                            setShowLoginModal(false);
                        }}
                    />
                </div>
            </>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAF7F2]">
            <div className="bg-[#FAF7F2] md:px-[120px] p-[20px] md:pt-[60px] md:pb-[20px] gap-[16px] flex items-center">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="hover:bg-gray-100 rounded-full p-1 shrink-0 flex items-center justify-center"
                    aria-label="Go back"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <g clip-path="url(#clip0_3371_46)">
                            <path d="M5 12H19" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M5 12L11 18" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M5 12L11 6" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        </g>
                        <defs>
                            <clipPath id="clip0_3371_46">
                                <rect width="24" height="24" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                </button>
                <h1 className="text-[20px] font-medium text-black leading-none">All Bookings</h1>
            </div>

            <div className="md:px-[120px] p-[20px] md:py-[20px] flex flex-col gap-[16px] pb-10 w-full">
                <div className="flex gap-[4px]">
                    <button
                        onClick={() => setActiveTab('stays')}
                        className={`flex items-center justify-center gap-[3px] p-[10px] rounded-full font-regular text-[14px]  transition-colors ${activeTab === 'stays'
                            ? 'bg-black text-white '
                            : 'bg-transparent text-black border-[1px] border-[#E5E3DF]'
                            }`}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            className={activeTab === 'stays' ? 'text-white' : 'text-black'}
                        >
                            <path d="M8.96685 13.1523H5.09425C4.97538 13.1523 4.8791 13.056 4.8791 12.9371V10.3554C4.8791 9.16942 5.84403 8.20396 7.03055 8.20396C8.21707 8.20396 9.18199 9.16942 9.18199 10.3554V12.9371C9.18199 13.056 9.08571 13.1523 8.96685 13.1523ZM5.30939 12.722H8.7517V10.3554C8.7517 9.40662 7.97933 8.63425 7.03055 8.63425C6.08176 8.63425 5.30939 9.40662 5.30939 10.3554V12.722Z" fill="currentColor" />
                            <path d="M1.65167 11.0008C1.5328 11.0008 1.43653 10.9046 1.43653 10.7857V6.26767C1.43653 6.1488 1.5328 6.05252 1.65167 6.05252C1.77054 6.05252 1.86681 6.1488 1.86681 6.26767V10.7857C1.86681 10.9046 1.77054 11.0008 1.65167 11.0008Z" fill="currentColor" />
                            <path d="M12.4087 10.5706C12.2898 10.5706 12.1936 10.4743 12.1936 10.3554V6.26767C12.1936 6.1488 12.2898 6.05252 12.4087 6.05252C12.5276 6.05252 12.6238 6.1488 12.6238 6.26767V10.3554C12.6238 10.4743 12.5276 10.5706 12.4087 10.5706Z" fill="currentColor" />
                            <path d="M1.00664 6.69785C0.941019 6.69785 0.876476 6.66773 0.834523 6.61179L0.18909 5.75121C0.151439 5.70119 0.137455 5.63773 0.150901 5.57695C0.164348 5.51617 0.203074 5.46346 0.257936 5.43388L6.92741 1.77643C6.99303 1.74093 7.07209 1.74092 7.13718 1.77804L13.5915 5.43549C13.691 5.49197 13.7292 5.61675 13.6781 5.71894L13.2478 6.57952C13.222 6.63116 13.1757 6.67096 13.1203 6.68817C13.0649 6.70538 13.0047 6.70054 12.9536 6.67257L7.03122 3.49973L1.10883 6.67257C1.07656 6.68978 1.0416 6.69785 1.00718 6.69785H1.00664ZM0.68177 5.69151L1.06742 6.20571L6.92849 3.06568C6.99195 3.03179 7.06833 3.03179 7.1318 3.06568L12.9606 6.18849L13.2005 5.70873L7.02799 2.21102L0.681232 5.69151H0.68177Z" fill="currentColor" />
                            <path d="M8.53652 7.77366C8.13635 7.77366 7.79911 7.49881 7.70337 7.12822H6.35818C6.26244 7.49881 5.9252 7.77366 5.52503 7.77366C5.05064 7.77366 4.66446 7.38747 4.66446 6.91308C4.66446 6.7614 4.70533 6.61349 4.78171 6.48279C4.70533 6.35209 4.66446 6.20418 4.66446 6.0525C4.66446 5.57811 5.05064 5.19193 5.52503 5.19193C5.9252 5.19193 6.26244 5.46677 6.35818 5.83736H7.70337C7.79911 5.46677 8.13635 5.19193 8.53652 5.19193C9.01091 5.19193 9.39709 5.57811 9.39709 6.0525C9.39709 6.20418 9.35622 6.35209 9.27984 6.48279C9.35622 6.61349 9.39709 6.7614 9.39709 6.91308C9.39709 7.38747 9.01091 7.77366 8.53652 7.77366ZM6.16993 6.69794H7.89108C8.00995 6.69794 8.10623 6.79421 8.10623 6.91308C8.10623 7.15028 8.29932 7.34337 8.53652 7.34337C8.77371 7.34337 8.9668 7.15028 8.9668 6.91308C8.9668 6.77969 8.90603 6.68341 8.85439 6.62693C8.7807 6.54518 8.7807 6.42093 8.85439 6.33864C8.90549 6.28217 8.9668 6.18589 8.9668 6.0525C8.9668 5.81531 8.77371 5.62221 8.53652 5.62221C8.29932 5.62221 8.10623 5.81531 8.10623 6.0525C8.10623 6.17137 8.00995 6.26765 7.89108 6.26765H6.16993C6.05106 6.26765 5.95478 6.17137 5.95478 6.0525C5.95478 5.81531 5.76169 5.62221 5.5245 5.62221C5.2873 5.62221 5.09421 5.81531 5.09421 6.0525C5.09421 6.18589 5.15498 6.28217 5.20662 6.33864C5.28031 6.4204 5.28031 6.54464 5.20662 6.6264C5.15552 6.68341 5.09421 6.77915 5.09421 6.91254C5.09421 7.14974 5.2873 7.34283 5.5245 7.34283C5.76169 7.34283 5.95478 7.14974 5.95478 6.91254C5.95478 6.79367 6.05106 6.6974 6.16993 6.6974V6.69794Z" fill="currentColor" />
                            <path d="M3.80353 13.1522H0.791512C0.722128 13.1522 0.657585 13.1189 0.617245 13.063C0.576906 13.007 0.56561 12.9344 0.587663 12.8688L1.01795 11.5779C1.047 11.4903 1.12929 11.4311 1.2218 11.4311H3.37324C3.46576 11.4311 3.54805 11.4903 3.57709 11.5779L4.00738 12.8688C4.02943 12.9344 4.01814 13.0065 3.9778 13.063C3.93746 13.1189 3.87238 13.1522 3.80353 13.1522ZM1.09002 12.722H3.50502L3.21834 11.8614H1.37724L1.09056 12.722H1.09002Z" fill="currentColor" />
                            <path d="M13.7 13.1523H13.2697C13.1508 13.1523 13.0546 13.056 13.0546 12.9372V12.3122C13.0546 12.068 12.9395 11.8389 12.7458 11.7001C12.3887 11.443 11.996 11.3677 11.6389 11.486C11.3114 11.5958 11.0543 11.8572 10.951 12.1863C10.9338 12.2417 10.8945 12.288 10.8423 12.3143C10.7902 12.3402 10.7294 12.3439 10.6745 12.3251C10.6116 12.303 10.5433 12.2917 10.4723 12.2917C10.1162 12.2917 9.82686 12.5811 9.82686 12.9372C9.82686 13.056 9.73058 13.1523 9.61171 13.1523H0.361045C0.242178 13.1523 0.145901 13.056 0.145901 12.9372C0.145901 12.8183 0.242178 12.722 0.361045 12.722H9.41862C9.51866 12.2315 9.95325 11.8615 10.4728 11.8615C10.5202 11.8615 10.5675 11.8641 10.6132 11.8706C10.7869 11.4989 11.1086 11.2096 11.5028 11.0783C11.995 10.9143 12.5253 11.0111 12.997 11.351C13.3025 11.571 13.4843 11.9303 13.4843 12.3127V12.7226H13.6995C13.8183 12.7226 13.9146 12.8189 13.9146 12.9377C13.9146 13.0566 13.8183 13.1529 13.6995 13.1529L13.7 13.1523Z" fill="currentColor" />
                        </svg>
                        <span className="leading-none">Stays</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('events')}
                        className={` flex items-center justify-center gap-[3px] p-[10px] rounded-full font-normal text-[14px] transition-colors ${activeTab === 'events'
                            ? 'bg-black text-white'
                            : 'bg-transparent text-black border-[1px] border-[#E5E3DF]'
                            }`}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            className={activeTab === 'events' ? 'text-white' : 'text-black'}
                        >
                            <path d="M7.1298 13.6326C7.10858 13.6326 7.08789 13.6294 7.06721 13.623C4.11619 12.7148 0.551956 9.29861 0.551956 6.41815C0.551956 3.71327 1.97999 1.75 3.94697 1.75C5.58189 1.75 6.71073 2.97009 7.1298 3.50905C7.54887 2.97009 8.67772 1.75 10.3126 1.75C12.2796 1.75 13.7076 3.71275 13.7076 6.41762C13.7076 9.29861 10.1434 12.7154 7.1924 13.6235C7.17224 13.6299 7.15102 13.6331 7.1298 13.6331V13.6326ZM3.94697 2.17438C1.99696 2.17438 0.976333 4.30899 0.976333 6.41815C0.976333 9.10499 4.32679 12.3037 7.1298 13.1981C9.93281 12.3037 13.2833 9.10499 13.2833 6.41762C13.2833 4.309 12.2626 2.17438 10.3126 2.17438C8.48834 2.17438 7.32024 3.9679 7.30857 3.98594C7.26985 4.04694 7.20248 4.08355 7.1298 4.08355C7.05766 4.08355 6.99029 4.04694 6.95103 3.98594C6.93936 3.9679 5.76596 2.17385 3.94697 2.17385V2.17438Z" fill="currentColor" />
                            <path d="M9.67548 12.1473C9.55824 12.1473 9.46329 12.0523 9.46329 11.9351C9.46329 10.5262 9.07445 9.99569 8.84211 9.67846C8.7201 9.5119 8.61453 9.36761 8.61453 9.17664C8.61453 8.98567 8.68403 8.83342 8.81505 8.72043C9.09726 8.47801 9.57946 8.52522 9.6898 8.54007C10.4701 8.53477 10.8749 8.10614 11.0473 7.85046C11.121 7.74065 11.1603 7.61228 11.1603 7.47913C11.1603 7.36189 11.0653 7.26694 10.9481 7.26694C10.938 7.26694 10.9279 7.26641 10.9173 7.26482L10.7895 7.24731C10.4006 7.19533 9.86804 7.12424 9.52482 6.7805C9.49352 6.7492 9.47284 6.70888 9.46541 6.66539C9.45639 6.61287 9.22033 5.35724 7.76525 5.35724C6.17171 5.35724 5.0068 6.6134 5.0068 7.26694C5.0068 7.68442 5.08053 7.71041 5.22747 7.76187C5.32508 7.79635 5.45929 7.84356 5.5813 7.96557C5.69694 8.08068 5.76908 8.22285 5.83911 8.35971C5.97066 8.61752 6.05023 8.75226 6.27993 8.75226C6.58707 8.75226 6.81677 8.66473 6.96265 8.49233C7.18597 8.22869 7.24273 7.75657 7.13134 7.08976C7.11224 6.97412 7.19022 6.86484 7.30586 6.84574C7.42256 6.82665 7.53078 6.90463 7.54988 7.02027C7.68249 7.81651 7.59391 8.40374 7.28623 8.76711C7.05601 9.03925 6.71704 9.17717 6.27993 9.17717C5.77916 9.17717 5.59509 8.81645 5.46088 8.55333C5.40359 8.44087 5.34895 8.33425 5.28105 8.26635C5.22747 8.21277 5.16488 8.19049 5.08637 8.16291C4.81052 8.06583 4.58242 7.93109 4.58242 7.26747C4.58242 6.34922 5.94945 4.9334 7.76525 4.9334C9.35772 4.9334 9.78316 6.19274 9.86698 6.51951C10.1147 6.72957 10.5184 6.78315 10.8462 6.82718L10.964 6.84362C11.3077 6.85211 11.5846 7.13432 11.5846 7.48019C11.5846 7.69768 11.5205 7.90828 11.399 8.08864C11.1783 8.41594 10.6611 8.96551 9.67495 8.96551C9.66434 8.96551 9.65373 8.96445 9.64312 8.96339C9.5158 8.94429 9.21238 8.93952 9.09143 9.04402C9.06543 9.0663 9.03891 9.09972 9.03891 9.1777C9.03891 9.23021 9.10628 9.32199 9.18479 9.42861C9.4479 9.78721 9.88766 10.3877 9.88766 11.9361C9.88766 12.0534 9.79271 12.1483 9.67548 12.1483V12.1473Z" fill="currentColor" />
                            <path d="M4.37048 12.1472C4.33281 12.1472 4.29515 12.1372 4.26067 12.1165C4.16041 12.0555 4.12858 11.925 4.18959 11.8247C5.23514 10.1044 5.43142 8.531 5.43354 8.51509C5.44733 8.39892 5.55184 8.31457 5.66907 8.32943C5.78524 8.34322 5.86853 8.44825 5.85527 8.56496C5.84731 8.63232 5.64944 10.2418 4.55296 12.0454C4.51317 12.1112 4.44315 12.1472 4.37154 12.1472H4.37048Z" fill="currentColor" />
                        </svg>
                        <span className="leading-none">Events</span>
                    </button>
                </div>

            <div>
                {loading ? (
                    <div className="flex items-center justify-center py-[40px]">
                        <div className="w-8 h-8 border-2 border-[#FF5E4C] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : bookings.length > 0 ? (
                    <div className="space-y-[8px]">
                        {bookings.map((booking, index) => {
                            const statusBadge =
                              activeTab === 'stays'
                                ? getStayBookingBadge(booking)
                                : getEventStatusBadge(booking.status, booking.paymentStatus);
                            const checkIn = activeTab === 'stays' ? booking.checkIn : booking.show?.date;
                            const checkOut = activeTab === 'stays' ? booking.checkOut : null;
                            const nights = checkOut ? calculateNights(checkIn, checkOut) : null;
                            const stayInfo =
                              activeTab === 'stays' ? getStayInfoFromBooking(booking) : null;

                            return (
                                <div
                                    key={booking.bookingId || index}
                                    onClick={() => handleBookingClick(booking)}
                                    className="bg-white rounded-[14px] p-[20px] shadow-[0_4px_4px_rgba(0, 0, 0, 0.04)] cursor-pointer hover:shadow-md transition-shadow"
                                >
                                    {/* Booking ID and Status */}
                                    <div className="flex items-center justify-start gap-[6px]">
                                        <span className="text-[18px] font-normal text-black uppercase leading-none">
                                            #{booking.bookingId || 'N/A'}
                                        </span>
                                        <span
                                            className={`p-[6px] rounded-full text-[12px] font-medium leading-none ${statusBadge.bgColor} ${statusBadge.textColor}`}
                                        >
                                            {statusBadge.text}
                                        </span>
                                    </div>
                                    {/* Divider */}
                                    <div className='flex my-[20px] items-center justify-center'>
                                        <svg width="100%" height="1px">
                                            <line
                                                x1="0" y1="1" x2="100%" y2="1"
                                                stroke="#f0f0f0"
                                                strokeWidth="2"
                                                strokeDasharray="10 4"
                                            />
                                        </svg>
                                    </div>
                                    {/* Hotel/Event Info */}
                                    <div className="flex gap-[8px]">
                                        <img
                                            src={
                                                activeTab === 'stays'
                                                    ? stayInfo?.image || '/assets/logo.svg'
                                                    : booking.event?.images?.[0] || '/assets/logo.svg'
                                            }
                                            alt={
                                                activeTab === 'stays'
                                                    ? stayInfo?.title || 'Stay'
                                                    : booking.event?.title || 'Event'
                                            }
                                            className="w-[100px] h-[100px] rounded-[14px] object-cover"
                                        />
                                        <div className="flex-1 py-[12px]">
                                            <h3 className="text-[18px] font-medium text-black mb-[4px] leading-[1.1]">
                                                {activeTab === 'stays'
                                                    ? stayInfo?.title || 'Stay'
                                                    : booking.event?.title || 'Event'}
                                            </h3>
                                            {activeTab === 'events' && (
                                                <>
                                                    {/* Ticket Type */}
                                                    {booking.lineItems && booking.lineItems.length > 0 && (
                                                        <p className="text-[16px] font-medium text-[#78736A] leading-[1.1] mb-[8px]">
                                                            {booking.lineItems[0].quantity || 1} x {booking.lineItems[0].title || booking.lineItems[0].ticketTypeName || 'Ticket Type 1'}
                                                        </p>
                                                    )}
                                                    {/* Location with pin icon */}
                                                    <div className="flex items-center gap-[4px] mb-[4px]">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                            <g clip-path="url(#clip0_3371_266)">
                                                                <path d="M5.25 6.41666C5.25 6.88079 5.43437 7.3259 5.76256 7.65409C6.09075 7.98228 6.53587 8.16666 7 8.16666C7.46413 8.16666 7.90925 7.98228 8.23744 7.65409C8.56563 7.3259 8.75 6.88079 8.75 6.41666C8.75 5.95253 8.56563 5.50741 8.23744 5.17922C7.90925 4.85103 7.46413 4.66666 7 4.66666C6.53587 4.66666 6.09075 4.85103 5.76256 5.17922C5.43437 5.50741 5.25 5.95253 5.25 6.41666Z" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                                                                <path d="M10.2999 9.7166L7.82481 12.1917C7.60606 12.4102 7.30949 12.533 7.00027 12.533C6.69106 12.533 6.39449 12.4102 6.17573 12.1917L3.70007 9.7166C3.04744 9.06394 2.60301 8.23242 2.42297 7.32718C2.24292 6.42194 2.33535 5.48365 2.68857 4.63094C3.04178 3.77823 3.63992 3.04941 4.40735 2.53664C5.17477 2.02387 6.07701 1.75018 6.99998 1.75018C7.92295 1.75018 8.82519 2.02387 9.59262 2.53664C10.36 3.04941 10.9582 3.77823 11.3114 4.63094C11.6646 5.48365 11.757 6.42194 11.577 7.32718C11.397 8.23242 10.9525 9.06394 10.2999 9.7166Z" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                                                            </g>
                                                            <defs>
                                                                <clipPath id="clip0_3371_266">
                                                                    <rect width="14" height="14" fill="white" />
                                                                </clipPath>
                                                            </defs>
                                                        </svg>
                                                        <p className="text-[14px] text-[#78736A] leading-[1.1]">
                                                            {`${booking.event?.city || ''} ${booking.event?.state || ''}`.trim() ||
                                                                booking.event?.address ||
                                                                'Location not available'}
                                                        </p>
                                                    </div>
                                                    {/* Date */}
                                                    {booking.show && (
                                                        <p className="text-[13px] text-[#000] leading-[1.1]">
                                                            {formatDate(booking.show.date)} {new Date(booking.show.date).getFullYear()}
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                            {activeTab === 'stays' && (
                                                <p className="text-[12px] text-[#bebebe] leading-[1.1]">
                                                    {stayInfo?.location || 'Location not available'}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    {activeTab === 'stays' && (
                                        <div className='flex my-[20px] items-center justify-center'>
                                            <svg width="100%" height="1px">
                                                <line
                                                    x1="0" y1="1" x2="100%" y2="1"
                                                    stroke="#f0f0f0"
                                                    strokeWidth="2"
                                                    strokeDasharray="10 4"
                                                />
                                            </svg>
                                        </div>
                                    )}
                                    {/* Check-in/Check-out Details */}
                                    {activeTab === 'stays' && checkIn && checkOut && (
                                        <div className="flex items-center justify-between">
                                            {/* Check-in */}
                                            <div className="flex flex-col gap-[6px]">
                                                <div className="flex items-center gap-[4px]">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="14"
                                                        height="14"
                                                        viewBox="0 0 14 14"
                                                        fill="none"
                                                    >
                                                        <g clipPath="url(#clip0_2846_6297)">
                                                            <path
                                                                d="M2.33203 4.08341C2.33203 3.774 2.45495 3.47725 2.67374 3.25846C2.89253 3.03966 3.18928 2.91675 3.4987 2.91675H10.4987C10.8081 2.91675 11.1049 3.03966 11.3237 3.25846C11.5424 3.47725 11.6654 3.774 11.6654 4.08341V11.0834C11.6654 11.3928 11.5424 11.6896 11.3237 11.9084C11.1049 12.1272 10.8081 12.2501 10.4987 12.2501H3.4987C3.18928 12.2501 2.89253 12.1272 2.67374 11.9084C2.45495 11.6896 2.33203 11.3928 2.33203 11.0834V4.08341Z"
                                                                stroke="black"
                                                                strokeWidth="0.583333"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                            <path
                                                                d="M9.33203 1.75V4.08333"
                                                                stroke="black"
                                                                strokeWidth="0.583333"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                            <path
                                                                d="M4.66797 1.75V4.08333"
                                                                stroke="black"
                                                                strokeWidth="0.583333"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                            <path
                                                                d="M2.33203 6.41675H11.6654"
                                                                stroke="black"
                                                                strokeWidth="0.583333"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </g>
                                                        <defs>
                                                            <clipPath id="clip0_2846_6297">
                                                                <rect width="14" height="14" fill="white" />
                                                            </clipPath>
                                                        </defs>
                                                    </svg>
                                                    <span className="text-[12px] text-[#999999]">Check in</span>
                                                </div>
                                                <div>
                                                    <div className="font-normal text-[16px] leading-none mb-[4px]">
                                                        {formatDate(checkIn)}
                                                    </div>
                                                    <div className="text-[12px] font-medium leading-none text-[#636363]">
                                                        {getCheckInTime()}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Duration */}
                                            <div className="text-center">
                                                <span className="text-[#FF5E4C] font-medium text-[12px] leading-none">
                                                    {nights} {nights === 1 ? 'Night' : 'Nights'}
                                                </span>
                                            </div>

                                            {/* Check-out */}
                                            <div className="flex flex-col gap-[6px] items-end">
                                                <div className="flex items-center gap-[4px]">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="14"
                                                        height="14"
                                                        viewBox="0 0 14 14"
                                                        fill="none"
                                                    >
                                                        <g clipPath="url(#clip0_2846_6297)">
                                                            <path
                                                                d="M2.33203 4.08341C2.33203 3.774 2.45495 3.47725 2.67374 3.25846C2.89253 3.03966 3.18928 2.91675 3.4987 2.91675H10.4987C10.8081 2.91675 11.1049 3.03966 11.3237 3.25846C11.5424 3.47725 11.6654 3.774 11.6654 4.08341V11.0834C11.6654 11.3928 11.5424 11.6896 11.3237 11.9084C11.1049 12.1272 10.8081 12.2501 10.4987 12.2501H3.4987C3.18928 12.2501 2.89253 12.1272 2.67374 11.9084C2.45495 11.6896 2.33203 11.3928 2.33203 11.0834V4.08341Z"
                                                                stroke="black"
                                                                strokeWidth="0.583333"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                            <path
                                                                d="M9.33203 1.75V4.08333"
                                                                stroke="black"
                                                                strokeWidth="0.583333"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                            <path
                                                                d="M4.66797 1.75V4.08333"
                                                                stroke="black"
                                                                strokeWidth="0.583333"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                            <path
                                                                d="M2.33203 6.41675H11.6654"
                                                                stroke="black"
                                                                strokeWidth="0.583333"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </g>
                                                        <defs>
                                                            <clipPath id="clip0_2846_6297">
                                                                <rect width="14" height="14" fill="white" />
                                                            </clipPath>
                                                        </defs>
                                                    </svg>
                                                    <span className="text-[12px] text-[#999999]">Check out</span>
                                                </div>
                                                <div className="text-end">
                                                    <div className="font-normal text-[16px] leading-none mb-[4px]">
                                                        {formatDate(checkOut)}
                                                    </div>
                                                    <div className="text-[12px] font-medium leading-none text-[#636363]">
                                                        {getCheckOutTime()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}


                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-[60px]">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-[16px]">
                            {activeTab === 'stays' ? (
                                <Home size={32} className="text-gray-400" />
                            ) : (
                                <Heart size={32} className="text-gray-400" />
                            )}
                        </div>
                        <p className="text-[16px] font-medium text-gray-600 mb-[8px]">
                            {activeTab === 'stays' ? 'No stay bookings yet' : 'No events booked yet'}
                        </p>
                        <p className="text-[14px] text-gray-400 text-center max-w-sm px-4">
                            {activeTab === 'stays'
                              ? 'Your stay bookings will appear here with their current status.'
                              : 'Only event bookings with completed payment are shown here.'}
                        </p>
                    </div>
                )}
            </div>
            </div>
        </div>
    );
};

export default AllBookings;

